import { Combobox, Field, TagsInput, useCombobox, useFilter, useListCollection, useTagsInput } from "@chakra-ui/react"
import { LandingpageRubricsProps } from "../../api/landingpage"
import { useEffect, useId, useMemo } from "react"
import { Controller } from "react-hook-form"

function LandingpageRubrics({
    form,
    rubrics,
    categoryList,
}: LandingpageRubricsProps) {
    const { control, formState: {errors} } = form
    const { contains } = useFilter({ sensitivity: "base" })

    const allItems = useMemo(() => {
        return categoryList?.data?.map(c => ({
            value: c.id,
            label: c.name,
        })) ?? []
    }, [categoryList])

    const { collection, filter, reset } = useListCollection({
        initialItems: allItems,
        filter: contains,
    })

    useEffect(() => {
        if (categoryList?.data) {
            reset()
        }
    }, [categoryList])

    const idToLabel = useMemo(() => {
        const map = new Map<string, string>()
        allItems.forEach(i => map.set(i.value, i.label))
        return map
    }, [allItems])

    const labelToId = useMemo(() => {
        const map = new Map<string, string>()
        allItems.forEach(i => map.set(i.label, i.value))
        return map
    }, [allItems])


    const uid = useId()

    return (
        <Field.Root mt={4}>
            <Controller
                control={control}
                name="rubrics.landingpage_rubrics"
                defaultValue={
                    rubrics?.map(r => ({
                        fk_category_id: r.fk_category.id
                    })) ?? []
                }
                render={({ field }) => {
                    const selectedIds = field.value?.map(v => v.fk_category_id) ?? []
                    const selectedLabels = selectedIds
                        .map(id => idToLabel.get(id))
                        .filter(Boolean) as string[]


                    const tags = useTagsInput({
                        ids: { input: `input_${uid}`, control: `control_${uid}` },
                        value: selectedLabels,
                        onValueChange(details) {
                            const labels = details.value

                            const ids = labels
                                .map(label => labelToId.get(label))
                                .filter(Boolean) as string[]

                            field.onChange(ids.map(id => ({ fk_category_id: id })))
                        },
                    })

                    const combobox = useCombobox({
                        ids: { input: `input_${uid}`, control: `control_${uid}` },
                        collection,
                        onInputValueChange(e) {
                            filter(e.inputValue)
                        },
                        allowCustomValue: false,
                        selectionBehavior: "clear",
                        onValueChange(e) {
                            const item = e.items[0]
                            if (item) {
                                tags.addValue(item.label)
                            }
                        },
                    })

                    return (
                        <Combobox.RootProvider value={combobox} data-testid="input_lp_rubrics">
                            <TagsInput.RootProvider value={tags}>
                                <TagsInput.Control data-testid="tags_lp_rubrics">
                                    {selectedLabels.map((tag, i) => (
                                        <TagsInput.Item key={tag} index={i} value={tag}>
                                            <TagsInput.ItemPreview bgColor="main" color="secondary">
                                                <TagsInput.ItemText>{tag}</TagsInput.ItemText>
                                                <TagsInput.ItemDeleteTrigger />
                                            </TagsInput.ItemPreview>
                                        </TagsInput.Item>
                                    ))}

                                    <Combobox.Input unstyled asChild>
                                        <TagsInput.Input
                                            placeholder="Kategorie hinzufügen..."
                                        />
                                    </Combobox.Input>
                                </TagsInput.Control>

                                <Combobox.Positioner>
                                <Combobox.Content>
                                    <Combobox.Empty>Keine Kategorie gefunden</Combobox.Empty>
                                    {collection.items.map((item) => (
                                        <Combobox.Item item={item} key={item.value}>
                                            <Combobox.ItemText>{item.label}</Combobox.ItemText>
                                        </Combobox.Item>
                                    ))}
                                </Combobox.Content>
                                </Combobox.Positioner>
                            </TagsInput.RootProvider>
                        </Combobox.RootProvider>
                    )
                }}
            />

            {errors?.rubrics?.landingpage_rubrics &&
                <Field.ErrorText>{errors?.rubrics?.landingpage_rubrics.message}</Field.ErrorText>
            }
        </Field.Root>
    )
}

export default LandingpageRubrics
